import Button from "@/components/Button";
import PremiumHero from "@/components/PremiumPage/PremiumHero";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useFeatureFlagEnabled, useFeatureFlagPayload } from "posthog-js/react";
import PlanCard from "./PlanCard";
import RecipeCarousel from "./RecipeCarousel";
import Testimonials from "./Testimonials";

const PremiumPageLayout = ({ layout }) => {
  return (
    <div
      className={clsx(
        "MarketingPageLayout",
        "overflow-x-hidden overflow-y-hidden font-spatial"
      )}
    >
      {layout.map((block) => (
        <Block key={block.id} block={block} />
      ))}
    </div>
  );
};

const Block = ({ block }) => {
  return (
    <div key={block.id}>
      <Component block={block} />
    </div>
  );
};

const Component = ({ block }) => {
  const router = useRouter();

  const isPremiumPage = router.pathname === "/premium";

  const isExperimentEnabled = useFeatureFlagEnabled(
    "cro-web-141-copy-testing-headline-and-features-update-on-premium-page"
  );

  const experimentPayload = useFeatureFlagPayload(
    "cro-web-141-copy-testing-headline-and-features-update-on-premium-page"
  );

  switch (block.typeHandle) {
    case "hero": {
      return (
        <PremiumHero
          {...block}
          {...(isPremiumPage &&
          isExperimentEnabled &&
          typeof experimentPayload === "object"
            ? experimentPayload
            : {})}
        />
      );
    }

    case "planCards": {
      const { heading, mobPlans } = block;

      return (
        <div className={clsx("container max-w-screen-lg")}>
          <div
            className={clsx(
              "my-20 flex flex-col flex-wrap items-center justify-center lg:flex-row"
            )}
          >
            <div className="mb-14 max-w-[600px] text-center text-[32px] font-medium lg:text-5xl lg:leading-[43px]">
              {heading}
            </div>

            <div className={clsx("flex w-full flex-col md:gap-6 lg:flex-row")}>
              <div
                className={clsx("w-full place-content-center gap-6 md:flex")}
              >
                {mobPlans.map((plan, index) => {
                  const planType = index === 0 ? "yearly" : "monthly";
                  return (
                    <div
                      key={plan.id}
                      className={clsx("mb-6 last:mb-0 md:mb-0 md:basis-1/2")}
                    >
                      <PlanCard key={plan.id} plan={plan} planType={planType} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case "testimonials": {
      const { testimonials, heading, subheading } = block;

      return (
        <div className="bg-gray-50 py-24">
          <div className="container">
            <div className="mb-[40px] flex flex-col items-center gap-6 lg:mb-[40px]">
              <div
                className="max-w-[630px] text-center text-[32px] font-medium leading-7 lg:text-5xl lg:leading-[43px]"
                dangerouslySetInnerHTML={{ __html: heading }}
              ></div>
              <div className="text-center font-body text-sm font-normal text-gray-800 lg:text-lg">
                {subheading}
              </div>
            </div>
          </div>
          <div className="mx-auto max-w-screen-2xl px-6">
            <Testimonials testimonials={testimonials} />
          </div>
        </div>
      );
    }

    case "recipeCarousel": {
      const { recipes, heading, subheading, target } = block;

      return (
        <div className="container mb-[105px] mt-20">
          <div className="mb-10 flex flex-col items-center justify-center">
            <div className="max-w-[680px] text-center text-[32px] font-medium lg:text-5xl">
              {heading}
            </div>
            <div className="my-6 max-w-[680px] text-center font-body text-sm font-normal lg:text-lg">
              {subheading}
            </div>
            <Button
              href={target.url}
              theme="light"
              className="w-[212px] py-[12px]"
            >
              {target.text}
            </Button>
          </div>

          <RecipeCarousel recipes={recipes} />
        </div>
      );
    }

    default:
      return <div>{block.typeHandle} in development</div>;
  }
};

export default PremiumPageLayout;
